import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { deletePrompt, listPrompts, createPrompt, updatePrompt, type AdminPrompt } from "@/integrations/backend/prompts";
import { useToast } from "@/hooks/use-toast";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { MoreVertical, Edit3, Trash2 } from "lucide-react";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";

type AdminPromptRow = AdminPrompt;

const promptsQueryKey = queryKeys.prompts.all;

export default function PromptsAdminView() {
  const queryClient = useQueryClient();
  const promptsQuery = useQuery({
    queryKey: promptsQueryKey,
    queryFn: listPrompts,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...promptsQueryKey]),
  });
  const prompts = (promptsQuery.data ?? []) as AdminPrompt[];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdminPrompt | null>(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const [promptToDelete, setPromptToDelete] = useState<AdminPrompt | null>(null);

  useEffect(() => {
    if (promptsQuery.isError && promptsQuery.error) {
      toast({ title: "Error loading prompts", description: (promptsQuery.error as Error).message, variant: "destructive" });
    }
  }, [promptsQuery.isError, promptsQuery.error, toast]);

  const openCreate = () => { setEditing(null); setName(""); setSlug(""); setDescription(""); setOpen(true); };
  const openEdit = (p: AdminPrompt) => { setEditing(p); setName(p.name); setSlug(p.slug); setDescription(p.description); setOpen(true); };
  const save = async () => {
    if (!name.trim() || !slug.trim()) {
      toast({ title: "Required", description: "Name and slug are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updatePrompt(editing.id, { name: name.trim(), slug: slug.trim(), description: description.trim() });
        toast({ title: "Saved", description: "Prompt updated" });
      } else {
        await createPrompt({ name: name.trim(), slug: slug.trim(), description: description.trim() });
        toast({ title: "Saved", description: "Prompt created" });
      }
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: promptsQueryKey });
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message || String(e), variant: "destructive" });
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!promptToDelete) return;
    const id = promptToDelete.id;
    setPromptToDelete(null);
    try {
      await deletePrompt(id);
      toast({ title: "Deleted", description: "Prompt removed" });
      await queryClient.invalidateQueries({ queryKey: promptsQueryKey });
    } catch (e: any) {
      toast({ title: "Delete failed", description: e.message || String(e), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Prompts</h1>
          <p className="text-gray-600">Manage system prompts used across the platform.</p>
        </div>
        <Button onClick={openCreate}>Add Prompt</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Prompts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
            <TableBody>
                {!promptsQuery.isLoading && prompts.map((p)=> (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>/{p.slug}</TableCell>
                    <TableCell>{p.description}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon" aria-label="Actions">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={()=>openEdit(p)}>
                            <Edit3 className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={()=>setPromptToDelete(p)} className="text-destructive focus:text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {(!promptsQuery.isLoading && prompts.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-gray-500">No prompts yet.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white text-black">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Prompt' : 'Add Prompt'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <Input placeholder="Name" value={name} onChange={e=>setName(e.target.value)} className="bg-white text-black placeholder:text-gray-500" />
            <Input placeholder="Slug" value={slug} onChange={e=>setSlug(e.target.value)} className="bg-white text-black placeholder:text-gray-500" />
            <Textarea placeholder="Description" value={description} onChange={e=>setDescription(e.target.value)} className="bg-white text-black placeholder:text-gray-500" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="text-black border-gray-300 hover:bg-gray-100">Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(promptToDelete)} onOpenChange={(open)=>!open && setPromptToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prompt?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The prompt will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}




